import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import { pdf } from '@react-pdf/renderer'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import LinkIcon from '@mui/icons-material/Link'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined'
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined'
import OutboundOutlinedIcon from '@mui/icons-material/OutboundOutlined'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import API from '../../../services/API'
import SafetyIncidentLogPDF from './SafetyIncidentLogPDF'
import { CRMContext } from '../../CRM'
import PageHeader from '../../kit/PageHeader'
import SectionCard from '../../kit/SectionCard'
import KeyValueList from '../../kit/KeyValueList'
import ConfirmDialog from '../../kit/ConfirmDialog'
import { SoftChip } from '../../kit/Chips'
import { formatDate, formatDateTime, fromNow, initials, toDateInput } from '../../kit/helpers'
import {
  ACTION_STATUS_COLOR,
  ACTION_STATUS_OPTIONS,
  ACTION_TYPE_OPTIONS,
  CLASSIFICATION_COLOR,
  CLASSIFICATION_OPTIONS,
  CONTRIBUTING_FACTOR_OPTIONS,
  HARM_COLOR,
  HARM_OPTIONS,
  LIKELIHOOD_OPTIONS,
  MODULE_OPTIONS,
  PLATFORM_OPTIONS,
  PRIORITY_COLOR,
  PRIORITY_OPTIONS,
  REPORTING_BODIES,
  REPORTING_STATE_COLOR,
  REPORTING_STATE_OPTIONS,
  SAFETY_CASE_IMPACT_OPTIONS,
  SEVERITY_OPTIONS,
  SOURCE_OPTIONS,
  STATUS_COLOR,
  STATUS_OPTIONS,
  YES_NO_UNKNOWN,
  fullName,
  isOpen,
  openActions,
  rateRisk,
  riskTone,
  slaState,
} from './constants'

const BASE = '/admincrm/clinical-risk/safety-incidents'

const HAZARD_MODULES = [
  'Events',
  'Clinical Governance',
  'Patient Care Records',
  'Fleet Management',
  'Personnel',
  'Patient Transport',
  'Platform & Security',
  'Clinical Content & Algorithms',
  'Third-Party Services',
  'Infrastructure & Operations',
  'Organisation & Governance',
]

const toLocalInput = (value) => (value ? moment(value).format('YYYY-MM-DDTHH:mm') : '')
const listToText = (list) => (list || []).join(', ')
const textToList = (text) =>
  String(text || '')
    .split(/[\n,;]+/)
    .map((x) => x.trim())
    .filter(Boolean)

// ---------------------------------------------------------------------------
// Small form helpers
// ---------------------------------------------------------------------------

const SelectField = ({
  label,
  value,
  onChange,
  options,
  allowEmpty,
  emptyLabel = 'Not set',
  ...props
}) => (
  <TextField
    select
    label={label}
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    fullWidth
    size="small"
    {...props}
  >
    {allowEmpty && <MenuItem value="">{emptyLabel}</MenuItem>}
    {options.map((option) => (
      <MenuItem key={option} value={option}>
        {option}
      </MenuItem>
    ))}
  </TextField>
)

const TextArea = ({ label, value, onChange, minRows = 3, ...props }) => (
  <TextField
    label={label}
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    multiline
    minRows={minRows}
    fullWidth
    size="small"
    {...props}
  />
)

const DateTimeField = ({ label, value, onChange, dateOnly = false, ...props }) => (
  <TextField
    type={dateOnly ? 'date' : 'datetime-local'}
    label={label}
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    InputLabelProps={{ shrink: true }}
    fullWidth
    size="small"
    {...props}
  />
)

// A card whose body switches between a read-only view and an edit form.
// `toDraft` builds the form state from the incident; `toPayload` turns the
// draft into the partial body sent to PUT. A review comment is offered on
// every save so the timeline records why.
const EditableCard = ({
  title,
  subtitle,
  icon,
  incident,
  toDraft,
  toPayload,
  view,
  form,
  onSave,
  disabled = false,
  extraAction,
}) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(() => toDraft(incident))
  const [comment, setComment] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!editing) setDraft(toDraft(incident))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident, editing])

  const update = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }))

  const save = async () => {
    setSaving(true)
    try {
      const ok = await onSave({ ...toPayload(draft), reviewComment: comment })
      if (ok) {
        setEditing(false)
        setComment('')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <SectionCard
      title={title}
      subtitle={subtitle}
      icon={icon}
      action={
        <>
          {extraAction}
          {!editing ? (
            <Button
              size="small"
              startIcon={<EditOutlinedIcon />}
              onClick={() => setEditing(true)}
              disabled={disabled}
            >
              Edit
            </Button>
          ) : (
            <>
              <Button size="small" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
        </>
      }
    >
      {editing ? (
        <Stack gap={2}>
          {form(draft, update)}
          <TextField
            label="Note for the timeline (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            size="small"
            fullWidth
          />
        </Stack>
      ) : (
        view(incident)
      )}
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

const ReportSection = ({ incident, onSave }) => (
  <EditableCard
    title="What was reported"
    subtitle="As the reporter gave it. Edit only to tidy a phoned-in report; the timeline records changes."
    icon={<DescriptionOutlinedIcon />}
    incident={incident}
    toDraft={(i) => ({
      title: i.title || '',
      description: i.description || '',
      immediateActions: i.immediateActions || '',
      module: i.module || '',
      platform: i.platform || 'Unknown',
      softwareVersion: i.softwareVersion || '',
      occurredAt: toLocalInput(i.occurredAt),
      isOngoing: i.isOngoing || 'Unknown',
      reporterHarm: i.reporterHarm || 'Unknown',
      patientsAffected: i.patientsAffected ?? '',
      recordReferences: listToText(i.recordReferences),
      source: i.source || 'In-app ticket',
    })}
    toPayload={(d) => ({
      ...d,
      occurredAt: d.occurredAt ? new Date(d.occurredAt).toISOString() : null,
      recordReferences: textToList(d.recordReferences),
    })}
    view={(i) => (
      <Stack gap={2}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {i.description}
        </Typography>
        {i.immediateActions && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              textTransform="uppercase"
            >
              Immediate actions taken
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {i.immediateActions}
            </Typography>
          </Box>
        )}
        <Divider />
        <KeyValueList
          columns={3}
          rows={[
            { label: 'Occurred', value: i.occurredAt ? formatDateTime(i.occurredAt) : 'Not given' },
            { label: 'Module', value: i.module || 'Not specified' },
            { label: 'Platform', value: i.platform },
            {
              label: "Reporter's harm assessment",
              render: <SoftChip color={HARM_COLOR[i.reporterHarm]} label={i.reporterHarm} />,
            },
            { label: 'Still happening at report', value: i.isOngoing },
            {
              label: 'Patients affected',
              value:
                i.patientsAffected === undefined || i.patientsAffected === null
                  ? 'Not given'
                  : i.patientsAffected,
            },
            { label: 'Record references', value: listToText(i.recordReferences) || 'None given' },
            { label: 'Software version', value: i.softwareVersion || 'Unknown' },
            {
              label: 'Attachments',
              value: i.attachmentsCount ? `${i.attachmentsCount} on the ClickUp ticket` : 'None',
            },
          ]}
          showEmpty
        />
      </Stack>
    )}
    form={(d, update) => (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Title"
            value={d.title}
            onChange={(e) => update('title', e.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <TextArea
            label="What happened"
            value={d.description}
            onChange={(v) => update('description', v)}
            minRows={5}
          />
        </Grid>
        <Grid item xs={12}>
          <TextArea
            label="Immediate actions taken"
            value={d.immediateActions}
            onChange={(v) => update('immediateActions', v)}
            minRows={2}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SelectField
            label="Module"
            value={d.module}
            onChange={(v) => update('module', v)}
            options={MODULE_OPTIONS}
            allowEmpty
            emptyLabel="Not specified"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SelectField
            label="Platform"
            value={d.platform}
            onChange={(v) => update('platform', v)}
            options={PLATFORM_OPTIONS}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DateTimeField
            label="Occurred at"
            value={d.occurredAt}
            onChange={(v) => update('occurredAt', v)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SelectField
            label="Source"
            value={d.source}
            onChange={(v) => update('source', v)}
            options={SOURCE_OPTIONS}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SelectField
            label="Reporter's harm assessment"
            value={d.reporterHarm}
            onChange={(v) => update('reporterHarm', v)}
            options={HARM_OPTIONS}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SelectField
            label="Still happening at report"
            value={d.isOngoing}
            onChange={(v) => update('isOngoing', v)}
            options={YES_NO_UNKNOWN}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            type="number"
            label="Patients affected"
            value={d.patientsAffected}
            onChange={(e) => update('patientsAffected', e.target.value)}
            inputProps={{ min: 0 }}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <TextField
            label="Record references (comma separated)"
            value={d.recordReferences}
            onChange={(e) => update('recordReferences', e.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Software version"
            value={d.softwareVersion}
            onChange={(e) => update('softwareVersion', e.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
      </Grid>
    )}
    onSave={onSave}
  />
)

const TriageSection = ({ incident, onSave }) => (
  <EditableCard
    title="Triage and risk"
    subtitle="CSO decision: is it a safety incident, how bad was it, how bad could it be, and how urgently do we act. Scored on the CRMP Appendix A matrix so it carries onto a hazard."
    icon={<MonitorHeartOutlinedIcon />}
    incident={incident}
    disabled={!isOpen(incident)}
    toDraft={(i) => ({
      status: i.status,
      classification: i.classification || 'Unclassified',
      priority: i.triage?.priority || '',
      actualHarm: i.triage?.actualHarm || 'Unknown',
      potentialHarm: i.triage?.potentialHarm || 'Unknown',
      likelihood: i.triage?.likelihood || '',
      severity: i.triage?.severity || '',
      notes: i.triage?.notes || '',
    })}
    toPayload={(d) => ({
      status: d.status,
      classification: d.classification,
      triage: {
        priority: d.priority,
        actualHarm: d.actualHarm,
        potentialHarm: d.potentialHarm,
        likelihood: d.likelihood,
        severity: d.severity,
        notes: d.notes,
      },
    })}
    view={(i) => {
      const t = i.triage || {}
      return (
        <Stack gap={2}>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <SoftChip color={STATUS_COLOR[i.status]} label={i.status} />
            <SoftChip
              color={CLASSIFICATION_COLOR[i.classification]}
              label={i.classification || 'Unclassified'}
            />
            {t.priority && <SoftChip color={PRIORITY_COLOR[t.priority]} label={t.priority} />}
            {t.riskRating ? (
              <Tooltip title={`${t.likelihood} likelihood / ${t.severity} severity`}>
                <span>
                  <SoftChip color={riskTone(t.riskRating)} label={`${t.riskRating} risk`} />
                </span>
              </Tooltip>
            ) : (
              <Chip
                label="Risk not scored"
                size="small"
                variant="outlined"
                sx={{ borderRadius: 1.5 }}
              />
            )}
          </Stack>
          <KeyValueList
            columns={3}
            rows={[
              {
                label: 'Actual harm',
                render: (
                  <SoftChip color={HARM_COLOR[t.actualHarm]} label={t.actualHarm || 'Unknown'} />
                ),
              },
              {
                label: 'Potential harm',
                render: (
                  <SoftChip
                    color={HARM_COLOR[t.potentialHarm]}
                    label={t.potentialHarm || 'Unknown'}
                  />
                ),
              },
              { label: 'Likelihood of recurrence', value: t.likelihood || 'Not scored' },
              { label: 'Severity', value: t.severity || 'Not scored' },
              {
                label: 'Acknowledged',
                value: t.acknowledgedAt
                  ? `${formatDateTime(t.acknowledgedAt)}${fullName(t.acknowledgedBy) ? ` by ${fullName(t.acknowledgedBy)}` : ''}`
                  : 'Not yet',
              },
              {
                label: 'Triaged',
                value: t.triagedAt
                  ? `${formatDateTime(t.triagedAt)}${fullName(t.triagedBy) ? ` by ${fullName(t.triagedBy)}` : ''}`
                  : 'Not yet',
              },
              { label: 'Triage notes', value: t.notes, fullWidth: true },
            ]}
            showEmpty
          />
        </Stack>
      )
    }}
    form={(d, update) => {
      const rating = rateRisk(d.likelihood, d.severity)
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <SelectField
              label="Status"
              value={d.status}
              onChange={(v) => update('status', v)}
              options={STATUS_OPTIONS.filter((s) => s !== 'Closed')}
              helperText="Use the Close button to close"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectField
              label="Classification"
              value={d.classification}
              onChange={(v) => update('classification', v)}
              options={CLASSIFICATION_OPTIONS}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SelectField
              label="Priority"
              value={d.priority}
              onChange={(v) => update('priority', v)}
              options={PRIORITY_OPTIONS}
              allowEmpty
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SelectField
              label="Actual harm"
              value={d.actualHarm}
              onChange={(v) => update('actualHarm', v)}
              options={HARM_OPTIONS}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SelectField
              label="Potential harm"
              value={d.potentialHarm}
              onChange={(v) => update('potentialHarm', v)}
              options={HARM_OPTIONS}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SelectField
              label="Likelihood of recurrence"
              value={d.likelihood}
              onChange={(v) => update('likelihood', v)}
              options={LIKELIHOOD_OPTIONS}
              allowEmpty
              emptyLabel="Not scored"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SelectField
              label="Severity"
              value={d.severity}
              onChange={(v) => update('severity', v)}
              options={SEVERITY_OPTIONS}
              allowEmpty
              emptyLabel="Not scored"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                height: '100%',
                minHeight: 40,
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                borderRadius: 1.5,
                bgcolor: alpha(riskTone(rating), 0.1),
                color: riskTone(rating),
                fontWeight: 700,
              }}
            >
              {rating ? `${rating} risk` : 'Risk: pick both to score'}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextArea
              label="Triage notes"
              value={d.notes}
              onChange={(v) => update('notes', v)}
              minRows={2}
            />
          </Grid>
        </Grid>
      )
    }}
    onSave={onSave}
  />
)

const InvestigationSection = ({ incident, onSave, currentUser }) => (
  <EditableCard
    title="Investigation"
    subtitle="Root cause and contributing factors. Check whether other tenants run the same code path."
    icon={<ScienceOutlinedIcon />}
    incident={incident}
    disabled={!isOpen(incident)}
    extraAction={
      isOpen(incident) && !incident.investigation?.lead && currentUser?._id ? (
        <Button
          size="small"
          onClick={() =>
            onSave({
              investigation: { lead: currentUser._id, startedAt: new Date().toISOString() },
              reviewComment: 'Investigation lead assigned',
            })
          }
        >
          Lead it myself
        </Button>
      ) : null
    }
    toDraft={(i) => ({
      summary: i.investigation?.summary || '',
      rootCause: i.investigation?.rootCause || '',
      contributingFactors: i.investigation?.contributingFactors || [],
      affectedVersions: listToText(i.investigation?.affectedVersions),
      otherTenantsAffected: i.investigation?.otherTenantsAffected || 'Unknown',
      affectedTenants: listToText(i.investigation?.affectedTenants),
      startedAt: toLocalInput(i.investigation?.startedAt),
      completedAt: toLocalInput(i.investigation?.completedAt),
    })}
    toPayload={(d) => ({
      investigation: {
        ...d,
        affectedVersions: textToList(d.affectedVersions),
        affectedTenants: textToList(d.affectedTenants),
        startedAt: d.startedAt ? new Date(d.startedAt).toISOString() : null,
        completedAt: d.completedAt ? new Date(d.completedAt).toISOString() : null,
      },
    })}
    view={(i) => {
      const inv = i.investigation || {}
      return (
        <KeyValueList
          columns={2}
          rows={[
            { label: 'Lead', value: fullName(inv.lead) || 'Unassigned' },
            {
              label: 'Started',
              value: inv.startedAt ? formatDateTime(inv.startedAt) : 'Not started',
            },
            { label: 'Summary', value: inv.summary, fullWidth: true },
            { label: 'Root cause', value: inv.rootCause, fullWidth: true },
            {
              label: 'Contributing factors',
              fullWidth: true,
              render: inv.contributingFactors?.length ? (
                <Stack direction="row" gap={0.5} flexWrap="wrap">
                  {inv.contributingFactors.map((f) => (
                    <SoftChip key={f} color="#5B48B0" label={f} />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.disabled">
                  —
                </Typography>
              ),
            },
            { label: 'Affected versions', value: listToText(inv.affectedVersions) },
            { label: 'Other tenants affected', value: inv.otherTenantsAffected },
            { label: 'Affected tenants', value: listToText(inv.affectedTenants), fullWidth: true },
            { label: 'Completed', value: inv.completedAt ? formatDateTime(inv.completedAt) : '' },
          ]}
          showEmpty
        />
      )
    }}
    form={(d, update) => (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextArea
            label="Summary of findings"
            value={d.summary}
            onChange={(v) => update('summary', v)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextArea
            label="Root cause"
            value={d.rootCause}
            onChange={(v) => update('rootCause', v)}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            Contributing factors
          </Typography>
          <Stack direction="row" gap={0.75} flexWrap="wrap" mt={0.5}>
            {CONTRIBUTING_FACTOR_OPTIONS.map((f) => {
              const on = d.contributingFactors.includes(f)
              return (
                <Chip
                  key={f}
                  label={f}
                  size="small"
                  variant={on ? 'filled' : 'outlined'}
                  color={on ? 'primary' : 'default'}
                  onClick={() =>
                    update(
                      'contributingFactors',
                      on
                        ? d.contributingFactors.filter((x) => x !== f)
                        : [...d.contributingFactors, f],
                    )
                  }
                />
              )
            })}
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Affected versions (comma separated)"
            value={d.affectedVersions}
            onChange={(e) => update('affectedVersions', e.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SelectField
            label="Other tenants affected?"
            value={d.otherTenantsAffected}
            onChange={(v) => update('otherTenantsAffected', v)}
            options={YES_NO_UNKNOWN}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Affected tenants (comma separated)"
            value={d.affectedTenants}
            onChange={(e) => update('affectedTenants', e.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DateTimeField
            label="Started"
            value={d.startedAt}
            onChange={(v) => update('startedAt', v)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DateTimeField
            label="Completed"
            value={d.completedAt}
            onChange={(v) => update('completedAt', v)}
          />
        </Grid>
      </Grid>
    )}
    onSave={onSave}
  />
)

const HazardSection = ({ incident, onAct, onSave }) => {
  const navigate = useNavigate()
  const [hazardID, setHazardID] = useState('')
  const [busy, setBusy] = useState(false)
  const [newOpen, setNewOpen] = useState(false)
  const [newDraft, setNewDraft] = useState({ title: incident.title || '', module: '' })
  const [error, setError] = useState('')

  const link = async () => {
    if (!hazardID.trim()) return
    setBusy(true)
    setError('')
    const res = await onAct('hazards', { hazardID: hazardID.trim(), op: 'link' })
    if (res?.ok) setHazardID('')
    else setError(res?.error || 'Could not link that hazard.')
    setBusy(false)
  }

  const raise = async () => {
    setBusy(true)
    setError('')
    const res = await onAct('hazards/new', {
      title: newDraft.title,
      module: newDraft.module || undefined,
    })
    setBusy(false)
    if (res?.ok) {
      setNewOpen(false)
      if (res.data?.hazard?._id) navigate(`/clinical-risk/${res.data.hazard._id}`)
    } else {
      setError(res?.error || 'Could not raise the hazard.')
    }
  }

  const linked = incident.linkedHazards || []
  const missing = (incident.hazards?.linkedHazardIDs || []).filter(
    (id) => !linked.some((h) => h.hazardID === id),
  )

  return (
    <EditableCard
      title="Hazard log"
      subtitle="CRMP §8: within two working days the incident is logged as a hazard or linked to an existing one."
      icon={<GppMaybeOutlinedIcon />}
      incident={incident}
      disabled={!isOpen(incident)}
      toDraft={(i) => ({
        newHazardRequired: i.hazards?.newHazardRequired || 'Unknown',
        notes: i.hazards?.notes || '',
      })}
      toPayload={(d) => ({ hazards: d })}
      extraAction={
        isOpen(incident) ? (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}>
            Raise hazard
          </Button>
        ) : null
      }
      view={(i) => (
        <Stack gap={2}>
          {linked.length === 0 && missing.length === 0 ? (
            <Alert severity={isOpen(i) ? 'warning' : 'info'} sx={{ borderRadius: 2 }}>
              No hazard linked yet.
              {i.hazards?.newHazardRequired === 'No' ? ' Marked as not needing a new hazard.' : ''}
            </Alert>
          ) : (
            <Stack gap={1}>
              {linked.map((h) => (
                <Stack
                  key={h.hazardID}
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={{ p: 1, borderRadius: 2, border: '1px solid #EEF2F7' }}
                >
                  <Link
                    component={RouterLink}
                    to={`/clinical-risk/${h._id}`}
                    fontWeight={700}
                    underline="hover"
                  >
                    {h.hazardID}
                  </Link>
                  <Typography variant="body2" flex={1} minWidth={0} noWrap>
                    {h.title}
                  </Typography>
                  {h.initialRisk?.riskRating && (
                    <SoftChip
                      color={riskTone(h.initialRisk.riskRating)}
                      label={h.initialRisk.riskRating}
                    />
                  )}
                  <SoftChip color="#64748B" label={h.status} />
                  {isOpen(i) && (
                    <Tooltip title="Unlink">
                      <IconButton
                        size="small"
                        onClick={() => onAct('hazards', { hazardID: h.hazardID, op: 'unlink' })}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              ))}
              {missing.map((id) => (
                <Typography key={id} variant="caption" color="text.disabled">
                  {id} (not found in the log)
                </Typography>
              ))}
            </Stack>
          )}
          {isOpen(i) && (
            <Stack direction="row" gap={1} alignItems="center">
              <TextField
                size="small"
                label="Link existing hazard"
                placeholder="HZD-000012"
                value={hazardID}
                onChange={(e) => setHazardID(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && link()}
                sx={{ maxWidth: 240 }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<LinkIcon />}
                onClick={link}
                disabled={busy || !hazardID.trim()}
              >
                Link
              </Button>
            </Stack>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          <KeyValueList
            rows={[
              { label: 'New hazard required?', value: i.hazards?.newHazardRequired },
              { label: 'Notes', value: i.hazards?.notes },
            ]}
            showEmpty
          />
          <Dialog open={newOpen} onClose={() => setNewOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Raise a hazard from this incident</DialogTitle>
            <DialogContent>
              <Stack gap={2} pt={1}>
                <Typography variant="body2" color="text.secondary">
                  Creates an Open entry in the Clinical register with source &quot;Incident
                  Report&quot;, carrying the triage risk as the initial score, and links it here.
                  Score and add controls on the hazard page.
                </Typography>
                <TextField
                  label="Hazard title"
                  value={newDraft.title}
                  onChange={(e) => setNewDraft((p) => ({ ...p, title: e.target.value }))}
                  fullWidth
                  size="small"
                />
                <SelectField
                  label="Hazard module"
                  value={newDraft.module}
                  onChange={(v) => setNewDraft((p) => ({ ...p, module: v }))}
                  options={HAZARD_MODULES}
                  allowEmpty
                  emptyLabel="Derive from the incident module"
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNewOpen(false)} disabled={busy}>
                Cancel
              </Button>
              <Button variant="contained" onClick={raise} disabled={busy || !newDraft.title.trim()}>
                Raise and open
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      )}
      form={(d, update) => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <SelectField
              label="New hazard required?"
              value={d.newHazardRequired}
              onChange={(v) => update('newHazardRequired', v)}
              options={YES_NO_UNKNOWN}
            />
          </Grid>
          <Grid item xs={12}>
            <TextArea
              label="Notes"
              value={d.notes}
              onChange={(v) => update('notes', v)}
              minRows={2}
            />
          </Grid>
        </Grid>
      )}
      onSave={onSave}
    />
  )
}

const ActionsSection = ({ incident, onAct }) => {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState({
    type: 'Corrective',
    description: '',
    dueDate: '',
    linkedTicketRef: '',
  })
  const [busy, setBusy] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)

  const add = async () => {
    setBusy(true)
    const res = await onAct('actions', {
      ...draft,
      dueDate: draft.dueDate ? new Date(draft.dueDate).toISOString() : undefined,
    })
    setBusy(false)
    if (res?.ok) {
      setOpen(false)
      setDraft({ type: 'Corrective', description: '', dueDate: '', linkedTicketRef: '' })
    }
  }

  const setStatus = (action, status) => onAct(`actions/${action._id}`, { status }, 'put')

  const actions = incident.actions || []
  const editable = isOpen(incident)

  return (
    <SectionCard
      title="Corrective and preventive actions"
      subtitle="Immediate containment, the fix, and what stops it recurring. Each has an owner, a due date and evidence when done."
      icon={<PlaylistAddCheckOutlinedIcon />}
      action={
        editable ? (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            Add action
          </Button>
        ) : null
      }
      dense
    >
      {actions.length === 0 ? (
        <Typography variant="body2" color="text.secondary" p={2}>
          No actions recorded yet.
        </Typography>
      ) : (
        <Stack divider={<Divider />}>
          {actions.map((action) => {
            const overdue =
              action.dueDate &&
              ['Open', 'In Progress'].includes(action.status) &&
              moment(action.dueDate).isBefore(moment(), 'day')
            return (
              <Stack
                key={action._id}
                direction={{ xs: 'column', sm: 'row' }}
                gap={1.5}
                p={2}
                alignItems={{ sm: 'center' }}
              >
                <Box flex={1} minWidth={0}>
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <SoftChip color="#557A95" label={action.type} />
                    <Typography variant="body2" fontWeight={600}>
                      {action.description}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    {fullName(action.owner) ? `${fullName(action.owner)} · ` : ''}
                    {action.dueDate ? (
                      <Box
                        component="span"
                        sx={{
                          color: overdue ? '#B42318' : 'inherit',
                          fontWeight: overdue ? 700 : 400,
                        }}
                      >
                        due {formatDate(action.dueDate)}
                        {overdue ? ' (overdue)' : ''}
                      </Box>
                    ) : (
                      'no due date'
                    )}
                    {action.completedAt ? ` · done ${formatDate(action.completedAt)}` : ''}
                    {action.linkedTicketRef ? ` · ${action.linkedTicketRef}` : ''}
                  </Typography>
                  {action.evidence && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Evidence: {action.evidence}
                    </Typography>
                  )}
                </Box>
                {editable ? (
                  <Stack direction="row" gap={0.5} alignItems="center">
                    <TextField
                      select
                      size="small"
                      value={action.status}
                      onChange={(e) => setStatus(action, e.target.value)}
                      sx={{ minWidth: 140 }}
                    >
                      {ACTION_STATUS_OPTIONS.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </TextField>
                    <IconButton size="small" onClick={() => setConfirmDelete(action)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ) : (
                  <SoftChip color={ACTION_STATUS_COLOR[action.status]} label={action.status} />
                )}
              </Stack>
            )
          })}
        </Stack>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add an action</DialogTitle>
        <DialogContent>
          <Stack gap={2} pt={1}>
            <SelectField
              label="Type"
              value={draft.type}
              onChange={(v) => setDraft((p) => ({ ...p, type: v }))}
              options={ACTION_TYPE_OPTIONS}
            />
            <TextArea
              label="What will be done"
              value={draft.description}
              onChange={(v) => setDraft((p) => ({ ...p, description: v }))}
              minRows={2}
            />
            <DateTimeField
              dateOnly
              label="Due"
              value={draft.dueDate}
              onChange={(v) => setDraft((p) => ({ ...p, dueDate: v }))}
            />
            <TextField
              label="ClickUp / release reference"
              value={draft.linkedTicketRef}
              onChange={(e) => setDraft((p) => ({ ...p, linkedTicketRef: e.target.value }))}
              size="small"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={add} disabled={busy || !draft.description.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Remove this action?"
        confirmLabel="Remove"
        confirmColor="error"
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => {
          await onAct(`actions/${confirmDelete._id}`, {}, 'delete')
          setConfirmDelete(null)
        }}
      >
        <Typography variant="body2">The removal is recorded on the timeline.</Typography>
      </ConfirmDialog>
    </SectionCard>
  )
}

const ReportingSection = ({ incident, onSave }) => (
  <EditableCard
    title="External reporting"
    subtitle="Who else must know. The CSO decides; the customer's own DCB0160 governance is told for anything confirmed."
    icon={<OutboundOutlinedIcon />}
    incident={incident}
    toDraft={(i) => ({
      bodies: Object.fromEntries(
        REPORTING_BODIES.map(({ key }) => [
          key,
          {
            state: i.externalReporting?.[key]?.state || 'Not assessed',
            reference: i.externalReporting?.[key]?.reference || '',
            date: toDateInput(i.externalReporting?.[key]?.date),
            notes: i.externalReporting?.[key]?.notes || '',
          },
        ]),
      ),
      dutyApplies: i.externalReporting?.dutyOfCandour?.applies || 'Unknown',
      dutyNotes: i.externalReporting?.dutyOfCandour?.notes || '',
    })}
    toPayload={(d) => ({
      externalReporting: {
        ...Object.fromEntries(
          Object.entries(d.bodies).map(([key, value]) => [
            key,
            { ...value, date: value.date ? new Date(value.date).toISOString() : null },
          ]),
        ),
        dutyOfCandour: { applies: d.dutyApplies, notes: d.dutyNotes },
      },
    })}
    view={(i) => (
      <Stack gap={1.5}>
        {REPORTING_BODIES.map(({ key, label, hint }) => {
          const entry = i.externalReporting?.[key] || {}
          const state = entry.state || 'Not assessed'
          return (
            <Stack key={key} direction="row" gap={1.5} alignItems="flex-start">
              <SoftChip
                color={REPORTING_STATE_COLOR[state]}
                label={state}
                sx={{ minWidth: 110, justifyContent: 'center' }}
              />
              <Box minWidth={0}>
                <Typography variant="body2" fontWeight={600}>
                  {label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[entry.reference, entry.date ? formatDate(entry.date) : null, entry.notes]
                    .filter(Boolean)
                    .join(' · ') || hint}
                </Typography>
              </Box>
            </Stack>
          )
        })}
        <Divider />
        <KeyValueList
          rows={[
            {
              label: 'Duty of candour applies',
              value: i.externalReporting?.dutyOfCandour?.applies || 'Unknown',
            },
            { label: 'Notes', value: i.externalReporting?.dutyOfCandour?.notes },
          ]}
          showEmpty
        />
      </Stack>
    )}
    form={(d, update) => (
      <Stack gap={2}>
        {REPORTING_BODIES.map(({ key, label, hint }) => {
          const entry = d.bodies[key]
          const set = (field, value) =>
            update('bodies', { ...d.bodies, [key]: { ...entry, [field]: value } })
          return (
            <Box key={key}>
              <Typography variant="body2" fontWeight={700}>
                {label}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                {hint}
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={3}>
                  <SelectField
                    label="State"
                    value={entry.state}
                    onChange={(v) => set('state', v)}
                    options={REPORTING_STATE_OPTIONS}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Reference"
                    value={entry.reference}
                    onChange={(e) => set('reference', e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <DateTimeField
                    dateOnly
                    label="Date"
                    value={entry.date}
                    onChange={(v) => set('date', v)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Notes"
                    value={entry.notes}
                    onChange={(e) => set('notes', e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
          )
        })}
        <Divider />
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <SelectField
              label="Duty of candour applies?"
              value={d.dutyApplies}
              onChange={(v) => update('dutyApplies', v)}
              options={YES_NO_UNKNOWN}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Duty of candour notes"
              value={d.dutyNotes}
              onChange={(e) => update('dutyNotes', e.target.value)}
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>
      </Stack>
    )}
    onSave={onSave}
  />
)

const CommunicationSection = ({ incident, onSave }) => (
  <EditableCard
    title="Customer communication"
    subtitle="What the affected organisation was told, when, and any workaround or safety notice issued (CRMP §8)."
    icon={<ForumOutlinedIcon />}
    incident={incident}
    toDraft={(i) => ({
      notifiedAt: toLocalInput(i.customerCommunication?.notifiedAt),
      method: i.customerCommunication?.method || '',
      safetyNoticeIssued: !!i.customerCommunication?.safetyNoticeIssued,
      safetyNoticeRef: i.customerCommunication?.safetyNoticeRef || '',
      workaroundProvided: i.customerCommunication?.workaroundProvided || '',
      releaseNotesRef: i.customerCommunication?.releaseNotesRef || '',
    })}
    toPayload={(d) => ({
      customerCommunication: {
        ...d,
        notifiedAt: d.notifiedAt ? new Date(d.notifiedAt).toISOString() : null,
      },
    })}
    view={(i) => {
      const c = i.customerCommunication || {}
      return (
        <KeyValueList
          columns={2}
          rows={[
            {
              label: 'Customer notified',
              value: c.notifiedAt
                ? `${formatDateTime(c.notifiedAt)}${c.method ? ` by ${c.method}` : ''}`
                : 'Not yet',
            },
            {
              label: 'Safety notice',
              value: c.safetyNoticeIssued
                ? `Issued${c.safetyNoticeRef ? ` (${c.safetyNoticeRef})` : ''}`
                : 'Not issued',
            },
            { label: 'Workaround provided', value: c.workaroundProvided, fullWidth: true },
            { label: 'Release notes reference', value: c.releaseNotesRef },
          ]}
          showEmpty
        />
      )
    }}
    form={(d, update) => (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <DateTimeField
            label="Customer notified at"
            value={d.notifiedAt}
            onChange={(v) => update('notifiedAt', v)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Method (email, call, meeting…)"
            value={d.method}
            onChange={(e) => update('method', e.target.value)}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={d.safetyNoticeIssued}
                onChange={(e) => update('safetyNoticeIssued', e.target.checked)}
              />
            }
            label="Safety notice issued"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Safety notice reference"
            value={d.safetyNoticeRef}
            onChange={(e) => update('safetyNoticeRef', e.target.value)}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextArea
            label="Workaround provided"
            value={d.workaroundProvided}
            onChange={(v) => update('workaroundProvided', v)}
            minRows={2}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Release notes reference"
            value={d.releaseNotesRef}
            onChange={(e) => update('releaseNotesRef', e.target.value)}
            size="small"
            fullWidth
          />
        </Grid>
      </Grid>
    )}
    onSave={onSave}
  />
)

const SafetyCaseSection = ({ incident, onSave }) => (
  <EditableCard
    title="Safety case impact"
    subtitle="DCB0129 §7.2.2 and §7.2.3: does this evidence undermine the Clinical Safety Case, and what was done about it."
    icon={<FactCheckOutlinedIcon />}
    incident={incident}
    toDraft={(i) => ({
      impact: i.safetyCase?.impact || 'Not assessed',
      notes: i.safetyCase?.notes || '',
      cscrUpdatedAt: toDateInput(i.safetyCase?.cscrUpdatedAt),
    })}
    toPayload={(d) => ({
      safetyCase: {
        ...d,
        cscrUpdatedAt: d.cscrUpdatedAt ? new Date(d.cscrUpdatedAt).toISOString() : null,
      },
    })}
    view={(i) => (
      <KeyValueList
        rows={[
          {
            label: 'Impact',
            render: (
              <SoftChip
                color={
                  i.safetyCase?.impact === 'CSCR update required'
                    ? '#B42318'
                    : i.safetyCase?.impact === 'Not assessed'
                      ? '#64748B'
                      : '#27803B'
                }
                label={i.safetyCase?.impact || 'Not assessed'}
              />
            ),
          },
          {
            label: 'CSCR updated',
            value: i.safetyCase?.cscrUpdatedAt ? formatDate(i.safetyCase.cscrUpdatedAt) : '',
          },
          { label: 'Notes', value: i.safetyCase?.notes, fullWidth: true },
        ]}
        showEmpty
      />
    )}
    form={(d, update) => (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <SelectField
            label="Impact on the safety case"
            value={d.impact}
            onChange={(v) => update('impact', v)}
            options={SAFETY_CASE_IMPACT_OPTIONS}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DateTimeField
            dateOnly
            label="CSCR updated on"
            value={d.cscrUpdatedAt}
            onChange={(v) => update('cscrUpdatedAt', v)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextArea
            label="Notes"
            value={d.notes}
            onChange={(v) => update('notes', v)}
            minRows={2}
          />
        </Grid>
      </Grid>
    )}
    onSave={onSave}
  />
)

const ClosureSection = ({ incident, onSave, onAct }) => {
  const [closeOpen, setCloseOpen] = useState(false)
  const [reopenOpen, setReopenOpen] = useState(false)
  const [draft, setDraft] = useState({
    summary: '',
    lessonsLearned: '',
    csoSignOff: false,
    closeWithOpenActions: false,
  })
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const open = isOpen(incident)
  const pending = openActions(incident)
  const checklist = [
    {
      label: 'Classified by the CSO',
      done: incident.classification && incident.classification !== 'Unclassified',
    },
    { label: 'Risk scored', done: !!incident.triage?.riskRating },
    {
      label: 'Hazard linked or raised, or marked not required',
      done:
        (incident.hazards?.linkedHazardIDs || []).length > 0 ||
        incident.hazards?.newHazardRequired === 'No',
    },
    { label: 'All actions done or cancelled', done: pending.length === 0 },
    {
      label: 'External reporting assessed',
      done: REPORTING_BODIES.every(
        ({ key }) =>
          (incident.externalReporting?.[key]?.state || 'Not assessed') !== 'Not assessed',
      ),
    },
    {
      label: 'Safety case impact assessed',
      done: (incident.safetyCase?.impact || 'Not assessed') !== 'Not assessed',
    },
    {
      label: 'Customer told (if a customer was affected)',
      done: !!incident.customerCommunication?.notifiedAt || !incident.tenant?.tenantId,
    },
  ]

  const close = async () => {
    setBusy(true)
    setError('')
    const res = await onAct('close', draft)
    setBusy(false)
    if (res?.ok) setCloseOpen(false)
    else setError(res?.error || 'Could not close the incident.')
  }

  const reopen = async () => {
    setBusy(true)
    setError('')
    const res = await onAct('reopen', { reason })
    setBusy(false)
    if (res?.ok) setReopenOpen(false)
    else setError(res?.error || 'Could not reopen the incident.')
  }

  if (open) {
    return (
      <SectionCard
        title="Closure"
        subtitle="What must be true before the CSO closes the record."
        icon={<LockOutlinedIcon />}
        action={
          <Button size="small" variant="contained" onClick={() => setCloseOpen(true)}>
            Close incident
          </Button>
        }
      >
        <Stack gap={0.75}>
          {checklist.map((item) => (
            <Stack key={item.label} direction="row" gap={1} alignItems="center">
              {item.done ? (
                <CheckCircleOutlineIcon fontSize="small" sx={{ color: '#27803B' }} />
              ) : (
                <RadioButtonUncheckedIcon fontSize="small" sx={{ color: '#94A3B8' }} />
              )}
              <Typography variant="body2" color={item.done ? 'text.primary' : 'text.secondary'}>
                {item.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <Dialog
          open={closeOpen}
          onClose={() => !busy && setCloseOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Close {incident.incidentID}</DialogTitle>
          <DialogContent>
            <Stack gap={2} pt={1}>
              {checklist.some((c) => !c.done) && (
                <Alert severity="warning">
                  {checklist.filter((c) => !c.done).length} checklist item(s) are not done. Closing
                  anyway is recorded on the timeline.
                </Alert>
              )}
              <TextArea
                label="Closure summary"
                value={draft.summary}
                onChange={(v) => setDraft((p) => ({ ...p, summary: v }))}
                minRows={3}
                required
              />
              <TextArea
                label="Lessons learned"
                value={draft.lessonsLearned}
                onChange={(v) => setDraft((p) => ({ ...p, lessonsLearned: v }))}
                minRows={2}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draft.csoSignOff}
                    onChange={(e) => setDraft((p) => ({ ...p, csoSignOff: e.target.checked }))}
                  />
                }
                label="I am the Clinical Safety Officer and sign this off"
              />
              {pending.length > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={draft.closeWithOpenActions}
                      onChange={(e) =>
                        setDraft((p) => ({ ...p, closeWithOpenActions: e.target.checked }))
                      }
                    />
                  }
                  label={`Close with ${pending.length} action(s) still open`}
                />
              )}
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCloseOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="contained" onClick={close} disabled={busy || !draft.summary.trim()}>
              Close incident
            </Button>
          </DialogActions>
        </Dialog>
      </SectionCard>
    )
  }

  return (
    <EditableCard
      title="Closure"
      subtitle="Closed record. Reopen if new evidence appears."
      icon={<LockOutlinedIcon />}
      incident={incident}
      extraAction={
        <Button size="small" onClick={() => setReopenOpen(true)}>
          Reopen
        </Button>
      }
      toDraft={(i) => ({
        summary: i.closure?.summary || '',
        lessonsLearned: i.closure?.lessonsLearned || '',
        signedOff: !!i.closure?.csoSignOff?.signedOff,
      })}
      toPayload={(d) => ({
        closure: {
          summary: d.summary,
          lessonsLearned: d.lessonsLearned,
          csoSignOff: { signedOff: d.signedOff },
        },
      })}
      view={(i) => (
        <Stack gap={2}>
          <KeyValueList
            columns={2}
            rows={[
              {
                label: 'Closed',
                value: `${formatDateTime(i.closure?.closedAt)}${fullName(i.closure?.closedBy) ? ` by ${fullName(i.closure.closedBy)}` : ''}`,
              },
              {
                label: 'CSO sign-off',
                render: i.closure?.csoSignOff?.signedOff ? (
                  <SoftChip
                    color="#27803B"
                    icon={<CheckCircleOutlineIcon />}
                    label={`Signed off ${formatDate(i.closure.csoSignOff.signedOffDate)}${fullName(i.closure.csoSignOff.signedOffBy) ? ` by ${fullName(i.closure.csoSignOff.signedOffBy)}` : ''}`}
                  />
                ) : (
                  <SoftChip color="#B54708" label="Awaiting CSO sign-off" />
                ),
              },
              { label: 'Closure summary', value: i.closure?.summary, fullWidth: true },
              { label: 'Lessons learned', value: i.closure?.lessonsLearned, fullWidth: true },
            ]}
            showEmpty
          />
          <Dialog
            open={reopenOpen}
            onClose={() => !busy && setReopenOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Reopen {i.incidentID}</DialogTitle>
            <DialogContent>
              <Stack gap={2} pt={1}>
                <TextArea
                  label="Why is it being reopened?"
                  value={reason}
                  onChange={setReason}
                  minRows={2}
                />
                {error && <Alert severity="error">{error}</Alert>}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReopenOpen(false)} disabled={busy}>
                Cancel
              </Button>
              <Button variant="contained" onClick={reopen} disabled={busy || !reason.trim()}>
                Reopen
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      )}
      form={(d, update) => (
        <Stack gap={2}>
          <TextArea
            label="Closure summary"
            value={d.summary}
            onChange={(v) => update('summary', v)}
          />
          <TextArea
            label="Lessons learned"
            value={d.lessonsLearned}
            onChange={(v) => update('lessonsLearned', v)}
            minRows={2}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={d.signedOff}
                onChange={(e) => update('signedOff', e.target.checked)}
              />
            }
            label="CSO sign-off"
          />
        </Stack>
      )}
      onSave={onSave}
    />
  )
}

const TIMELINE_KIND_COLOR = {
  system: '#94A3B8',
  note: '#64748B',
  status: '#268EBB',
  triage: '#B54708',
  investigation: '#5B48B0',
  action: '#0F6670',
  hazard: '#B42318',
  reporting: '#85276A',
  communication: '#00964C',
  closure: '#27803B',
}

const Timeline = ({ incident, onAct }) => {
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const items = useMemo(
    () =>
      [...(incident.timeline || [])].sort(
        (a, b) => moment(b.date).valueOf() - moment(a.date).valueOf(),
      ),
    [incident.timeline],
  )

  const submit = async () => {
    if (!note.trim()) return
    setBusy(true)
    const res = await onAct('timeline', { kind: 'note', comment: note.trim() })
    setBusy(false)
    if (res?.ok) setNote('')
  }

  return (
    <SectionCard
      title="Timeline"
      subtitle="Every change, in order. Notes are permanent."
      icon={<HistoryOutlinedIcon />}
    >
      <Stack gap={2}>
        <Stack gap={1}>
          <TextField
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note…"
            multiline
            minRows={2}
            size="small"
            fullWidth
            onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === 'Enter' && submit()}
          />
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              variant="contained"
              onClick={submit}
              disabled={busy || !note.trim()}
            >
              Add note
            </Button>
          </Stack>
        </Stack>
        <Stack>
          {items.map((item, index) => {
            const color = TIMELINE_KIND_COLOR[item.kind] || '#64748B'
            const author = fullName(item.by) || item.byName || ''
            return (
              <Stack
                key={item._id || index}
                direction="row"
                gap={1.5}
                sx={{
                  position: 'relative',
                  pb: 2,
                  '&:not(:last-of-type)::before': {
                    content: '""',
                    position: 'absolute',
                    left: 11,
                    top: 24,
                    bottom: 0,
                    width: 2,
                    bgcolor: '#EEF2F7',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: alpha(color, 0.15),
                    color,
                    fontSize: 10,
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    zIndex: 1,
                  }}
                >
                  {author ? initials(author) : '•'}
                </Box>
                <Box minWidth={0} flex={1}>
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <Typography
                      variant="caption"
                      sx={{
                        color,
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                      }}
                    >
                      {item.kind}
                    </Typography>
                    {author && (
                      <Typography variant="caption" color="text.secondary">
                        {author}
                      </Typography>
                    )}
                    <Tooltip title={formatDateTime(item.date)}>
                      <Typography variant="caption" color="text.disabled">
                        {fromNow(item.date)}
                      </Typography>
                    </Tooltip>
                  </Stack>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {item.comment}
                  </Typography>
                </Box>
              </Stack>
            )
          })}
        </Stack>
      </Stack>
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const SafetyIncidentDetail = () => {
  const { id } = useParams()
  const { user } = useContext(CRMContext)
  const [incident, setIncident] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [toast, setToast] = useState('')
  const [downloading, setDownloading] = useState(false)

  // One-incident report in the same chrome as the log export, with the full
  // timeline: the record a customer or regulator would be sent.
  const downloadPdf = async () => {
    setDownloading(true)
    try {
      const downloadedBy = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
      const blob = await pdf(
        <SafetyIncidentLogPDF incidents={[incident]} downloadedBy={downloadedBy} single />,
      ).toBlob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `AmbuNet_${incident.incidentID}_${moment().format('YYYY-MM-DD')}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error(e)
      setToast('Could not generate the PDF.')
    } finally {
      setDownloading(false)
    }
  }

  const absorb = (res, okStatus = [200, 201]) => {
    if (okStatus.includes(res.status)) {
      const data = res.data?.incident ?? res.data
      setIncident(data)
      return { ok: true, data: res.data }
    }
    const message = res.data?.error || 'That did not save.'
    setToast(message)
    return { ok: false, error: message }
  }

  useEffect(() => {
    setLoading(true)
    API.get(`${BASE}/${id}`)
      .then((res) => {
        if (res.status === 200) setIncident(res.data)
        else setError(res.data?.error || 'Could not load the incident.')
      })
      .catch(() => setError('Could not load the incident.'))
      .finally(() => setLoading(false))
  }, [id])

  const save = (partial) =>
    API.put(`${BASE}/${id}`, partial)
      .then((res) => {
        const result = absorb(res)
        if (result.ok) setToast('Saved')
        return result.ok
      })
      .catch(() => {
        setToast('That did not save.')
        return false
      })

  const act = (path, body = {}, method = 'post') =>
    API[method](`${BASE}/${id}/${path}`, body)
      .then((res) => absorb(res))
      .catch(() => ({ ok: false, error: 'Request failed.' }))

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    )
  }
  if (error || !incident)
    return <Alert severity="error">{error || 'Unable to load incident.'}</Alert>

  const sla = slaState(incident)
  const t = incident.triage || {}
  const open = isOpen(incident)

  return (
    <Stack gap={2.5}>
      <PageHeader
        eyebrow={`${incident.incidentID} · reported ${fromNow(incident.reportedAt)} · ${incident.source}`}
        title={incident.title}
        back="/safety-incidents"
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<DownloadOutlinedIcon />}
              onClick={downloadPdf}
              disabled={downloading}
            >
              {downloading ? 'Building PDF…' : 'Download PDF'}
            </Button>
            {incident.clickUp?.url && (
              <Button
                variant="outlined"
                endIcon={<OpenInNewIcon />}
                href={incident.clickUp.url}
                target="_blank"
                rel="noreferrer"
              >
                {incident.clickUp.ticketRef || 'ClickUp'}
              </Button>
            )}
            {open && !t.acknowledgedAt && (
              <Button variant="contained" color="error" onClick={() => act('acknowledge')}>
                Acknowledge
              </Button>
            )}
          </>
        }
      >
        <Stack direction="row" gap={1} flexWrap="wrap" mt={1} alignItems="center">
          <SoftChip color={STATUS_COLOR[incident.status]} label={incident.status} />
          <SoftChip
            color={CLASSIFICATION_COLOR[incident.classification]}
            label={incident.classification || 'Unclassified'}
          />
          {t.priority && <SoftChip color={PRIORITY_COLOR[t.priority]} label={t.priority} />}
          {t.riskRating && (
            <SoftChip color={riskTone(t.riskRating)} label={`${t.riskRating} risk`} />
          )}
          <SoftChip
            color={HARM_COLOR[t.actualHarm !== 'Unknown' ? t.actualHarm : incident.reporterHarm]}
            label={
              t.actualHarm && t.actualHarm !== 'Unknown'
                ? t.actualHarm
                : `${incident.reporterHarm} (reporter)`
            }
          />
          {open &&
            [sla.acknowledge, sla.classify]
              .filter((s) => s && s.key !== 'met')
              .map((s) => (
                <Typography
                  key={s.label}
                  variant="caption"
                  sx={{ color: s.key === 'overdue' ? '#B42318' : '#B54708', fontWeight: 700 }}
                >
                  {s.label}
                </Typography>
              ))}
        </Stack>
      </PageHeader>

      {open && incident.isOngoing === 'Yes' && (
        <Alert severity="error">
          The reporter said this was still happening when reported. Confirm containment first.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Stack gap={2}>
            <ReportSection incident={incident} onSave={save} />
            <TriageSection incident={incident} onSave={save} />
            <InvestigationSection incident={incident} onSave={save} currentUser={user} />
            <HazardSection incident={incident} onAct={act} onSave={save} />
            <ActionsSection incident={incident} onAct={act} />
            <ReportingSection incident={incident} onSave={save} />
            <CommunicationSection incident={incident} onSave={save} />
            <SafetyCaseSection incident={incident} onSave={save} />
            <ClosureSection incident={incident} onSave={save} onAct={act} />
          </Stack>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Stack gap={2}>
            <SectionCard title="Reporter and tenant" icon={<PersonOutlineIcon />}>
              <KeyValueList
                columns={1}
                rows={[
                  {
                    label: 'Tenant',
                    render: incident.tenant?.tenantId ? (
                      <Link
                        component={RouterLink}
                        to={`/tenants/${incident.tenant.tenantId}`}
                        underline="hover"
                        fontWeight={600}
                      >
                        {incident.tenant.name || incident.tenant.subdomain}
                      </Link>
                    ) : (
                      <Typography variant="body2">
                        {incident.tenant?.name || incident.tenant?.subdomain || 'Unknown'}
                      </Typography>
                    ),
                  },
                  { label: 'Region', value: incident.region },
                  { label: 'Reporter', value: incident.reporter?.name },
                  { label: 'Email', value: incident.reporter?.email },
                  { label: 'Phone', value: incident.reporter?.phone },
                  { label: 'Source', value: incident.source },
                  { label: 'Platform', value: incident.platform },
                ]}
                showEmpty
              />
            </SectionCard>
            <SectionCard title="Key dates" icon={<EventNoteOutlinedIcon />}>
              <KeyValueList
                columns={1}
                rows={[
                  {
                    label: 'Occurred',
                    value: incident.occurredAt ? formatDateTime(incident.occurredAt) : 'Not given',
                  },
                  { label: 'Reported', value: formatDateTime(incident.reportedAt) },
                  {
                    label: 'Acknowledged',
                    value: t.acknowledgedAt ? formatDateTime(t.acknowledgedAt) : 'Not yet',
                  },
                  {
                    label: 'Triaged',
                    value: t.triagedAt ? formatDateTime(t.triagedAt) : 'Not yet',
                  },
                  {
                    label: 'Investigation started',
                    value: incident.investigation?.startedAt
                      ? formatDateTime(incident.investigation.startedAt)
                      : '',
                  },
                  {
                    label: 'Investigation completed',
                    value: incident.investigation?.completedAt
                      ? formatDateTime(incident.investigation.completedAt)
                      : '',
                  },
                  {
                    label: 'Customer notified',
                    value: incident.customerCommunication?.notifiedAt
                      ? formatDateTime(incident.customerCommunication.notifiedAt)
                      : '',
                  },
                  {
                    label: 'Closed',
                    value: incident.closure?.closedAt
                      ? formatDateTime(incident.closure.closedAt)
                      : '',
                  },
                ]}
              />
            </SectionCard>
            <Timeline incident={incident} onAct={act} />
          </Stack>
        </Grid>
      </Grid>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        message={toast}
      />
    </Stack>
  )
}

export default SafetyIncidentDetail
